import React, { useMemo, useState } from "react";
import apiClient from "../api/apiClient";

type PricingForm = {
  name: string;
  phone: string;
  company: string;
  plan: "Free" | "Collections";
  explanation: string;
};

const INITIAL_FORM: PricingForm = {
  name: "",
  phone: "",
  company: "",
  plan: "Free",
  explanation: "",
};

type SubmitState = "idle" | "submitting" | "success" | "error";
const COLLECTIONS_PLATFORM_FEATURES = [
  "Everything in Free Plan",
  "3 Forecast scenarios",
  "Integration to ERP systems",
  "AI-powered collections automation",
  "Automated payment reminders & dunning",
  "DSO tracking & collections analytics",
  "Unlimited companies & bank accounts",
] as const;

const COLLECTIONS_MANAGED_FEATURES = [
  "We handle collections end-to-end",
  "Dedicated collections specialists",
] as const;

type PlanCardProps = {
  planName: string;
  price: string;
  period: string;
  blurb?: string;
  features: string[];
  ctaLabel: string;
  ctaHref?: string;
  ctaTargetBlank?: boolean;
  featured?: boolean;
  badge?: string;
  isCollections?: boolean;
  /** When true with isCollections, omit the “The platform” heading (e.g. single combined list). */
  hidePlatformSectionTitle?: boolean;
  managedFeatures?: string[];
};

function CheckIcon({ featured = false }: { featured?: boolean }) {
  return (
    <span
      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
        featured
          ? "border-emerald-400/35 bg-emerald-400/20"
          : "border-emerald-200 bg-emerald-50"
      }`}
    >
      <svg
        viewBox="0 0 9 7"
        fill="none"
        className="h-2.5 w-2.5"
        aria-hidden="true"
      >
        <path
          d="M1 3.5L3.2 5.8L8 1"
          stroke={featured ? "#52C878" : "#1B3D2F"}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function FeatureItem({
  text,
  featured = false,
}: {
  text: string;
  featured?: boolean;
}) {
  return (
    <li
      className={`flex items-start gap-2 text-[13.5px] leading-relaxed ${
        featured ? "text-white/85" : "text-emerald-950"
      }`}
    >
      <CheckIcon featured={featured} />
      <span>{text}</span>
    </li>
  );
}

function PlanCard({
  planName,
  price,
  period,
  blurb,
  features,
  ctaLabel,
  ctaHref = "#contact-sales-form",
  ctaTargetBlank = false,
  featured = false,
  badge,
  isCollections = false,
  hidePlatformSectionTitle = false,
  managedFeatures = [],
}: PlanCardProps) {
  const onCtaClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!ctaHref.startsWith("#")) return;
    const section = document.getElementById(ctaHref.slice(1));
    if (!section) return;
    event.preventDefault();
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cardClasses = featured
    ? "border-emerald-900 bg-emerald-900 text-white shadow-[0_16px_48px_rgba(15,36,25,0.22)] lg:scale-[1.04]"
    : isCollections
      ? "border-2 border-emerald-400 bg-gradient-to-b from-emerald-50 to-white"
      : "border-emerald-200 bg-white";

  return (
    <article
      className={`group relative rounded-3xl border p-7 transition duration-200 hover:-translate-y-1 hover:shadow-[0_18px_48px_rgba(15,36,25,0.10)] ${cardClasses}`}
    >
      {badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ${
            featured
              ? "bg-emerald-400 text-emerald-950"
              : "bg-emerald-900 text-emerald-200"
          }`}
        >
          {badge}
        </div>
      )}

      <p
        className={`mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] ${
          featured ? "text-white/50" : "text-emerald-700/75"
        }`}
      >
        {planName}
      </p>

      <div className="mb-1 flex items-end gap-1">
        <p
          className={`leading-none tracking-tight ${
            isCollections ? "text-4xl" : "text-5xl"
          } font-semibold ${featured ? "text-white" : "text-emerald-900"}`}
        >
          {price}
        </p>
      </div>

      <p
        className={`mb-4 text-sm ${featured ? "text-white/45" : "text-emerald-900/65"}`}
      >
        {period}
      </p>
      { blurb && <p
        className={`min-h-12 text-sm leading-relaxed ${featured ? "text-white/60" : "text-emerald-900/65"}`}
      >
        {blurb}
      </p>}
      <div
        className={`my-5 h-px ${featured ? "bg-white/15" : "bg-emerald-200"}`}
      />

      {isCollections && !hidePlatformSectionTitle && (
        <p className="mb-3 border-b border-dashed border-emerald-200 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-800">
          The platform
        </p>
      )}
      <ul className="mb-6 space-y-2.5">
        {features.map((feature) => (
          <FeatureItem key={feature} text={feature} featured={featured} />
        ))}
      </ul>

      <a
        href={ctaHref}
        onClick={onCtaClick}
        className={`block w-full rounded-xl border px-5 py-3 text-center text-sm font-medium transition ${
          featured || isCollections
            ? "border-emerald-400 bg-emerald-400 text-emerald-950 hover:border-emerald-500 hover:bg-emerald-500"
            : "border-emerald-900 text-emerald-900 hover:bg-emerald-900 hover:text-white"
        }`}
        target={ctaTargetBlank ? "_blank" : undefined}
        rel={ctaTargetBlank ? "noopener noreferrer" : undefined}
      >
        {ctaLabel}
      </a>
    </article>
  );
}

export default function PricingPage() {
  const [form, setForm] = useState<PricingForm>(INITIAL_FORM);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const isSubmitDisabled = useMemo(
    () =>
      submitState === "submitting" ||
      form.name.trim().length === 0 ||
      form.phone.trim().length === 0 ||
      form.company.trim().length === 0,
    [form.company, form.name, form.phone, form.plan, submitState],
  );

  const onChange =
    (field: keyof PricingForm) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
      if (submitState === "success") {
        setSubmitState("idle");
      }
      if (errorMessage) {
        setErrorMessage("");
      }
    };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitState("submitting");
    setErrorMessage("");

    try {
      await apiClient.post("/email/pricing-interest", {
        name: form.name,
        phone: form.phone,
        company: form.company,
        plan: form.plan,
        explanation: form.explanation,
      });
      setSubmitState("success");
      setForm(INITIAL_FORM);
    } catch (error: unknown) {
      const detail =
        error &&
        typeof error === "object" &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response &&
        "data" in error.response
          ? (
              error.response as {
                data?: { detail?: string | { message?: string } };
              }
            ).data?.detail
          : null;

      if (typeof detail === "string") {
        setErrorMessage(detail);
      } else if (detail && typeof detail === "object" && detail.message) {
        setErrorMessage(detail.message);
      } else {
        setErrorMessage("Could not send your request. Please try again.");
      }
      setSubmitState("error");
    }
  };

  return (
    <div className="overflow-auto bg-emerald-50 px-6 py-10">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
        <section className="overflow-hidden rounded-3xl border border-emerald-200 bg-slate-50 shadow-sm">
          <div className="px-6 py-12 text-center md:px-10">
            <div className="mb-10 inline-flex items-center gap-1.5">
              <span className="text-[26px] font-semibold tracking-tight text-emerald-900">
                Datamint
              </span>
              <svg
                viewBox="0 0 20 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mt-[-10px] h-3.5 w-3.5"
                aria-hidden="true"
              >
                <path
                  d="M10 1C5.5 1 2 5 2 9.5C2 15 7 20 10 23C13 20 18 15 18 9.5C18 5 14.5 1 10 1Z"
                  fill="#52C878"
                />
              </svg>
            </div>
            <p className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-[11px] font-semibold uppercase tracking-widest text-emerald-800">
              Pricing
            </p>
            <h2 className="mx-auto mt-5 max-w-2xl text-4xl font-semibold tracking-tight text-emerald-900 md:text-5xl">
              Simple, transparent pricing
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-emerald-900/65">
              Everything you need to take control of your cash flow. Start free
              and grow on your own terms.
            </p>
          </div>

          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 px-6 pb-14 md:grid-cols-2 xl:grid-cols-3">
            <PlanCard
              planName="Free"
              price="$0"
              period="Forever free · up to $3M ARR"
              blurb="For small businesses just getting started."
              features={[
                "2 currencies",
                "1 company",
                "1 bank account",
                "Cash flow dashboard",
                "Consolidated cash flow",
                "Customer aging dashboard",
                "Manual data entry",
              ]}
              ctaLabel="Get started free"
              ctaHref="#contact-sales-form"
            />
            <PlanCard
              isCollections
              badge="Finance teams' favorite"
              planName="Cashflow & Collection"
              price="Platform"
              period="Accelerate your collections"
              features={[...COLLECTIONS_PLATFORM_FEATURES]}
              managedFeatures={[...COLLECTIONS_MANAGED_FEATURES]}
              ctaLabel="Book a demo"
              ctaHref="https://calendly.com/itay-datamint/30min"
              ctaTargetBlank
            />
            <PlanCard
              isCollections
              hidePlatformSectionTitle
              badge="Platform + service"
              planName="Platform + service"
              price="Service"
              period="Software and specialists together"
              blurb="Full Collections capabilities plus our team running collections for you."
              features={[
                "Everything in Cashflow & Collection plan",
                ...COLLECTIONS_MANAGED_FEATURES,
              ]}
              ctaLabel="Book a demo"
              ctaHref="https://calendly.com/itay-datamint/30min"
              ctaTargetBlank
            />
          </div>
        </section>

        <section
          id="contact-sales-form"
          className="rounded-3xl border border-emerald-200 bg-white p-8 shadow-sm"
        >
          <h2 className="text-2xl font-semibold text-emerald-950">
            Contact sales
          </h2>
          <p className="mt-2 text-sm text-emerald-900/70">
            Fill in your details and we will send your request to our sales
            team.
          </p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="block text-sm font-medium text-emerald-950">
                Full Name
                <input
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  maxLength={120}
                  className="mt-1 w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm text-emerald-950 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </label>

              <label className="block text-sm font-medium text-emerald-950">
                Phone number
                <input
                  type="tel"
                  value={form.phone}
                  onChange={onChange("phone")}
                  required
                  maxLength={16}
                  className="mt-1 w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm text-emerald-950 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </label>
              <label className="block text-sm font-medium text-emerald-950">
                Company
                <input
                  type="text"
                  value={form.company}
                  onChange={onChange("company")}
                  required
                  maxLength={160}
                  className="mt-1 w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm text-emerald-950 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </label>
            </div>
            <label className="block text-sm font-medium text-emerald-950">
              Plan
              <select
                required
                value={form.plan}
                onChange={onChange("plan")}
                className="mt-1 w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm text-emerald-950 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              >
                <option value="Cashflow">Cashflow</option>
                <option value="Cashflow & Collections">Cashflow & Collection</option>
                <option value="Cashflow & Collections & Service">Cashflow & Collection & Service</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-emerald-950">
              Explanation (optional)
              <textarea
                value={form.explanation}
                onChange={onChange("explanation")}
                rows={5}
                maxLength={2000}
                className="mt-1 w-full rounded-xl border border-emerald-300 px-3 py-2 text-sm text-emerald-950 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              />
            </label>

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="inline-flex w-1/4 self-end items-center justify-center rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {submitState === "submitting" ? "Sending..." : "Send"}
              </button>

              {submitState === "success" && (
                <p className="text-sm text-emerald-700" role="status">
                  Sent successfully. We will be in touch shortly.
                </p>
              )}
              {submitState === "error" && (
                <p className="text-sm text-red-600" role="alert">
                  {errorMessage}
                </p>
              )}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
